import { useEffect, useState } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { syncMoodleUser } from '../services/moodleService';
import { checkIsAdmin, checkIsStakeholder } from '../services/adminService';

export interface AuthState {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isStakeholder: boolean;
  user: any;
  isLoading: boolean;
  error: string | null;
}

export const useAuth = (): AuthState => {
  const { instance, inProgress, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isAdmin: false,
    isStakeholder: false,
    user: null,
    isLoading: true,
    error: null
  });

  useEffect(() => {
    const checkStatus = async () => {
      if (inProgress === InteractionStatus.None) {
        if (account) {
          try {
            console.log('🛡️ [useAuth] Running admin/stakeholder checks...');

            // 1. Check Admin + Stakeholder status in parallel (independent checks)
            const [isAdminResult, isStakeholderResult] = await Promise.all([
              checkIsAdmin(),
              checkIsStakeholder(),
            ]);
            console.log('🛡️ [useAuth] isAdmin result:', isAdminResult, 'isStakeholder result:', isStakeholderResult);

            // 2. Sync user with Moodle (Non-blocking)
            let moodleUser = null;
            try {
                const nameParts = (account.name || '').split(' ');
                const firstName = nameParts[0] || 'User';
                const lastName = nameParts.slice(1).join(' ') || 'Portal';
                const syncResult = await syncMoodleUser(account.username, firstName, lastName);
                if (syncResult && syncResult.id) {
                    moodleUser = { ...account, moodleId: syncResult.id, id: syncResult.id };
                }
            } catch (moodleErr) {
                console.warn('⚠️ [useAuth] Moodle sync failed but continuing:', moodleErr);
            }

            setAuthState({
              isAuthenticated: true,
              isAdmin: isAdminResult,
              isStakeholder: isStakeholderResult,
              user: moodleUser || account,
              isLoading: false,
              error: null
            });
          } catch (err) {
            console.error('Auth check error:', err);
            setAuthState({
              isAuthenticated: true,
              isAdmin: false,
              isStakeholder: false,
              user: account,
              isLoading: false,
              error: null
            });
          }
        } else {
          setAuthState({
            isAuthenticated: false,
            isAdmin: false,
            isStakeholder: false,
            user: null,
            isLoading: false,
            error: null
          });
        }
      } else {
        setAuthState(prev => ({ ...prev, isLoading: true }));
      }
    };

    checkStatus();
  }, [account, inProgress]);

  return authState;
};